import axiosInstance from "../../axiosInstance";
import type {
  CreateSessionRequest,
  CreateSessionResponse,
  GetSessionResponse,
  SetUserInfoRequest,
  SetUserInfoResponse,
  BookSeatsRequest,
  BookSeatsResponse,
  ProcessPaymentRequest,
  ProcessPaymentResponse,
  ClaimGuestSessionRequest,
  ClaimGuestSessionResponse,
  GetOrdersResponse,
  ApplyCouponRequest,
  ApplyCouponResponse,
} from "./types";

/**
 * Create order session for a schedule
 * Creates a new order session for a specific event schedule
 */
export const createOrderSession = async (
  data: CreateSessionRequest
): Promise<CreateSessionResponse> => {
  const { data: response } = await axiosInstance.post<CreateSessionResponse>(
    "/user/orders/create-session",
    data
  );
  return response;
};

/**
 * Get order session details
 * Retrieves details of an existing order session
 */
export const getOrderSession = async (
  sessionId: string
): Promise<GetSessionResponse> => {
  const { data } = await axiosInstance.get<GetSessionResponse>(
    `/user/orders/session/${sessionId}`
  );

  return data;
};

/**
 * Set user information for order
 * Updates user contact information for the order session
 */
export const setUserInfo = async (
  data: SetUserInfoRequest
): Promise<SetUserInfoResponse> => {
  const { data: response } = await axiosInstance.post<SetUserInfoResponse>(
    "/user/orders/set-user-info",
    data
  );
  return response;
};

/**
 * Book seats for order
 * Books specific seats for the order
 */
export const bookSeats = async (
  data: BookSeatsRequest
): Promise<BookSeatsResponse> => {
  const { data: response } = await axiosInstance.post<BookSeatsResponse>(
    "/user/orders/book-seats",
    data
  );
  return response;
};

/**
 * Process payment for order
 * Processes payment for the order session using MercadoPago
 */
export const processPayment = async (
  data: ProcessPaymentRequest
): Promise<ProcessPaymentResponse> => {
  const { data: response } = await axiosInstance.post<ProcessPaymentResponse>(
    "/user/orders/process-payment",
    data
  );
  return response;
};

/**
 * Claim guest session after login
 * Allows authenticated users to claim ownership of a guest session
 */
export const claimGuestSession = async (
  data: ClaimGuestSessionRequest
): Promise<ClaimGuestSessionResponse> => {
  const { data: response } =
    await axiosInstance.post<ClaimGuestSessionResponse>(
      "/user/orders/claim-guest-session",
      data
    );
  return response;
};

/**
 * Get user orders
 * Retrieves all orders for the authenticated user
 */
export const getUserOrders = async (): Promise<GetOrdersResponse> => {
  const { data } = await axiosInstance.get<GetOrdersResponse>("/user/orders");
  return data;
};

/**
 * Get user order by ID
 * Retrieves a specific order belonging to the authenticated user
 */
export const getUserOrderById = async (orderId: string) => {
  const { data } = await axiosInstance.get<
    import("./types").GetOrderByIdResponse
  >(`/user/orders/${orderId}`);
  return data;
};

/**
 * Get order payment details
 * Retrieves live payment data from the payment provider (MercadoPago) for the given order
 */
export const getOrderPayment = async (orderId: string) => {
  const { data } = await axiosInstance.get<
    import("./types").GetOrderPaymentResponse
  >(`/user/orders/${orderId}/payment`);
  return data;
};

/**
 * Apply coupon to order session
 * Applies a coupon code to the given session and returns the updated session
 */
export const applyCoupon = async (
  data: ApplyCouponRequest
): Promise<ApplyCouponResponse> => {
  const { data: response } = await axiosInstance.post<ApplyCouponResponse>(
    "/user/orders/apply-coupon",
    data
  );
  return response;
};
